/**
 * Chat Input Component - OmniMind style
 * One composer box: attachments and voice on the left, prompt in the middle,
 * ink send button on the right.
 */

import React, { useState, useRef, useEffect } from 'react';
import { VoiceRecorder } from './VoiceRecorder';
import { FileUpload } from './FileUpload';

interface ChatInputProps {
  onSend: (content: string, attachments?: File[], voiceBlob?: Blob) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Ask about your business…',
}) => {
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 140) + 'px';
    }
  }, [input]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!input.trim() && !voiceBlob && attachments.length === 0) {
      return;
    }

    onSend(
      input.trim(),
      attachments.length > 0 ? attachments : undefined,
      voiceBlob || undefined
    );

    setInput('');
    setAttachments([]);
    setVoiceBlob(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = (files: File[]) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'text/plain',
      'image/png',
      'image/jpeg',
    ];

    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large (max 10MB)`);
        return false;
      }
      if (!allowedTypes.includes(file.type)) {
        alert(`File type ${file.type} is not allowed`);
        return false;
      }
      return true;
    });

    setAttachments((prev) => [...prev, ...validFiles]);
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleVoiceRecorded = (blob: Blob) => {
    setVoiceBlob(blob);
  };

  const hasContent = !!input.trim() || !!voiceBlob || attachments.length > 0;

  return (
    <div className="w-full">
      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map((file, index) => (
            <span key={index} className="tg tg-n" style={{ paddingRight: 5 }}>
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path d="M15.2 7l-6.6 6.6a2 2 0 102.8 2.8l6.4-6.6a4 4 0 00-5.6-5.6L5.8 10.8a6 6 0 108.5 8.5L20.5 13" />
              </svg>
              {file.name}
              <button
                type="button"
                onClick={() => removeAttachment(index)}
                title="Remove"
                style={{ marginLeft: 2, display: 'grid', placeItems: 'center' }}
              >
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.4}
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Voice preview */}
      {voiceBlob && (
        <div className="flex items-center gap-3 mb-2">
          <span className="tg tg-b">Voice message recorded</span>
          <button
            type="button"
            onClick={() => setVoiceBlob(null)}
            className="dim"
            style={{ fontSize: 11.5, fontWeight: 700 }}
          >
            Remove
          </button>
        </div>
      )}

      {/* Composer */}
      <div className="cbox">
        <div className="flex items-center gap-1" style={{ paddingBottom: 2 }}>
          <FileUpload onFilesSelected={handleFileSelect} disabled={disabled} />
          <VoiceRecorder onRecorded={handleVoiceRecorded} disabled={disabled} />
        </div>

        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
        />

        <button
          type="button"
          className="send"
          onClick={() => handleSubmit()}
          disabled={disabled || !hasContent}
          title="Send message"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 12 14-7-5 14-2.5-5.5z" />
          </svg>
        </button>
      </div>
    </div>
  );
};
